import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import Input from '../../../../../form/Input';
import * as FinanceiroAPI from '../../../../../../api/financeiro';
import {
    aceitaPreFinanceiro,
    resumirParcelas,
    sugerirParcelas,
    valorParaCampo,
} from '../../../../../../utils/movimento/preFinanceiro';

const chaveContexto = (d) =>
    JSON.stringify(
        [
            'id',
            'uuid',
            'empresaId',
            'operacaoId',
            'parceiroId',
            'prazoId',
            'formaId',
            'totalValor',
            'primeiraDataVencimento',
            'fixaDiaVencimento',
            'diaVencimento',
            'chNFe',
        ].map((k) => d?.[k] ?? null)
    );

export default function PreFinanceiroEditor({ dados, operacao, operacaoParametros, onChange, disabled = false }) {
    const [erro, setErro] = useState('');
    const [ocupado, setOcupado] = useState(false);
    const vivo = useRef(true);
    const atual = useRef(dados);
    atual.current = dados;
    useEffect(() => {
        vivo.current = true;
        return () => {
            vivo.current = false;
        };
    }, []);
    const pre = dados?.preFinanceiro;
    const ativo = Object.prototype.hasOwnProperty.call(dados || {}, 'preFinanceiro');
    const permitido = aceitaPreFinanceiro(dados, operacao, operacaoParametros);
    const parcelas = Array.isArray(pre?.parcelas) ? pre.parcelas : [];
    const bloqueado = disabled || ocupado;
    function enviar(valor) {
        onChange({ target: { name: 'preFinanceiro', value: valor, type: 'preFinanceiro' } });
    }
    function atualizar(i, campo, valor) {
        enviar({ versao: 1, parcelas: parcelas.map((p, index) => (index === i ? { ...p, [campo]: valor } : p)) });
    }
    async function preparar() {
        setErro('');
        const origem = chaveContexto(dados);
        if (
            ativo &&
            !window.confirm('Substituir as parcelas preparadas? Os valores, vencimentos e códigos digitados serão removidos.')
        )
            return;
        setOcupado(true);
        enviar({ versao: 1, parcelas, preparando: true });
        try {
            if (!dados.prazoId || !dados.formaId) throw new Error('Selecione a forma e o prazo antes de preparar as parcelas.');
            const [prazo, forma] = await Promise.all([
                FinanceiroAPI.buscaPrazoById(dados.prazoId),
                FinanceiroAPI.buscaFormaById(dados.formaId),
            ]);
            if (!vivo.current) return;
            if (chaveContexto(atual.current) !== origem)
                throw new Error('O movimento mudou durante a consulta. Confira os dados e prepare novamente.');
            if (!prazo || !forma || String(prazo.id) !== String(dados.prazoId) || String(forma.id) !== String(dados.formaId)) {
                throw new Error('Não foi possível consultar a forma e o prazo selecionados.');
            }
            enviar({
                versao: 1,
                parcelas: sugerirParcelas({
                    total: dados.totalValor,
                    quantidade: prazo.parcela,
                    primeiraDataVencimento: String(dados.primeiraDataVencimento || '').slice(0, 10),
                    diasEntreParcelas: prazo.diasEntreParcelas,
                    credito: forma.credito,
                    fixaDiaVencimento: dados.fixaDiaVencimento,
                    diaVencimento: dados.diaVencimento,
                }),
            });
        } catch (e) {
            if (vivo.current) {
                setErro(e.message || 'Não foi possível preparar as parcelas.');
                if (chaveContexto(atual.current) === origem) enviar({ versao: 1, parcelas });
            }
        } finally {
            if (vivo.current) setOcupado(false);
        }
    }
    function desativar() {
        if (window.confirm('Descartar as parcelas preparadas e voltar à geração automática pelo prazo?')) {
            enviar(undefined);
            setErro('');
        }
    }
    if (dados?.id) {
        return dados.preFinanceiroConfiguracao ? (
            <Alert severity="info" sx={{ my: 2 }}>
                Este movimento possui parcelas personalizadas. Os vencimentos e códigos serão preservados. A revisão dessas
                parcelas não está disponível nesta tela.
            </Alert>
        ) : null;
    }
    if (!permitido && !ativo) return null;
    let resumo, erroResumo;
    if (ativo) {
        try {
            resumo = resumirParcelas(parcelas, dados.totalValor);
        } catch (e) {
            erroResumo = e.message;
        }
    }
    return (
        <Box sx={{ my: 2 }}>
            <Typography variant="subtitle1">Parcelas da nota fiscal</Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
                Prepare as parcelas para ajustar vencimentos, valores e códigos antes de salvar o movimento.
            </Typography>
            {erro && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                    {erro}
                </Alert>
            )}
            {pre?.preparando && !ocupado && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                    A preparação foi interrompida. Clique em Refazer pelo prazo antes de salvar.
                </Alert>
            )}
            {!permitido && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                    A operação atual não permite estas parcelas. Revise as condições ou volte à geração automática.
                </Alert>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Button
                    type="button"
                    variant="outlined"
                    onClick={preparar}
                    disabled={bloqueado || !permitido}
                    title="O sistema calculará as parcelas pelo prazo e forma selecionados."
                >
                    {ocupado ? 'Preparando…' : ativo ? 'Refazer pelo prazo' : 'Preparar parcelas'}
                </Button>
                {ativo && (
                    <Button
                        type="button"
                        variant="outlined"
                        onClick={desativar}
                        disabled={bloqueado}
                        title="O sistema calculará as parcelas novamente pelo prazo e forma selecionados."
                    >
                        Usar geração automática
                    </Button>
                )}
            </Box>
            {ativo && (
                <>
                    {parcelas.map((p, i) => (
                        <Box
                            key={i}
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1,
                                alignItems: 'center',
                                mb: 1,
                                p: 1,
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                            }}
                        >
                            <Typography sx={{ minWidth: 80 }}>
                                Parcela {i + 1}/{parcelas.length}
                            </Typography>
                            <Input
                                text="Vencimento"
                                name={`preFinanceiroData${i}`}
                                type="date"
                                inputWidth="160px"
                                value={p.dataVencimento || ''}
                                disabled={bloqueado}
                                handleOnChange={(e) => atualizar(i, 'dataVencimento', e.target.value)}
                            />
                            <Input
                                text="Valor"
                                name={`preFinanceiroValor${i}`}
                                type="text"
                                inputWidth="140px"
                                textAlign="right"
                                placeholder="0,00"
                                value={p.valor ?? ''}
                                disabled={bloqueado}
                                handleOnChange={(e) => atualizar(i, 'valor', e.target.value)}
                            />
                            <Input
                                text="Código de Barras / Chave PIX"
                                name={`preFinanceiroCodigo${i}`}
                                type="text"
                                inputWidth="380px"
                                value={p.codBarraPIX || ''}
                                disabled={bloqueado}
                                barcodeScanner
                                barcodeScannerDisabled={bloqueado}
                                handleOnChange={(e) => atualizar(i, 'codBarraPIX', e.target.value)}
                            />
                            <Button
                                type="button"
                                color="error"
                                variant="contained"
                                disabled={bloqueado || parcelas.length <= 1}
                                onClick={() => {
                                    if (window.confirm(`Remover a parcela ${i + 1}? Confira a soma das parcelas restantes.`))
                                        enviar({ versao: 1, parcelas: parcelas.filter((_, n) => n !== i) });
                                }}
                            >
                                Remover
                            </Button>
                        </Box>
                    ))}
                    <Button
                        type="button"
                        disabled={bloqueado || parcelas.length >= 120 || !permitido}
                        onClick={() =>
                            enviar({ versao: 1, parcelas: [...parcelas, { valor: '', dataVencimento: '', codBarraPIX: '' }] })
                        }
                    >
                        Adicionar parcela
                    </Button>
                    {erroResumo ? (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                            {erroResumo}
                        </Alert>
                    ) : (
                        resumo && (
                            <Alert severity={resumo.diferenca === 0n ? 'success' : 'warning'} sx={{ mt: 1 }}>
                                Total do movimento: R$ {valorParaCampo(resumo.total)} · Soma das parcelas: R${' '}
                                {valorParaCampo(resumo.soma)}.
                                {resumo.diferenca !== 0n &&
                                    ` ${resumo.diferenca > 0n ? 'Faltam' : 'Excedem'} R$ ${valorParaCampo(resumo.diferenca < 0n ? -resumo.diferenca : resumo.diferenca)}.`}
                            </Alert>
                        )
                    )}
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        As parcelas serão gravadas ao salvar o movimento. Alterar o total não redistribui os valores digitados.
                    </Typography>
                </>
            )}
        </Box>
    );
}
